import React, { useState } from 'react';
import { Box, IconButton, LinearProgress, Skeleton, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { colors } from '../../styles/theme';
import {
  getMetaAdsStatusPillSx,
  metaAdsCardSx,
  metaAdsCardTitleSx,
  metaAdsCapabilityIconSx,
  metaAdsConnectHeroIconSx,
  metaAdsConnectHeroSx,
  metaAdsConnectHeroTitleSx,
  metaAdsCurrentStepCardSx,
  metaAdsIconButtonSx,
  metaAdsMutedTextSx,
  metaAdsPlainListSx,
  metaAdsProgressSx,
  metaAdsSkeletonSx,
  metaAdsStepIconSx,
  metaAdsStepNumberSx,
  metaAdsStepTitleSx,
  metaAdsWhyConnectSx,
  metaAdsWhyConnectTitleSx,
} from './metaAdsUi.styles';

/** Lo que pasa a estar disponible al conectar. Cuentas y vínculos son pasos; el resto, el alcance. */
const CAPABILITIES = {
  ad_account: { title: 'Cuentas', text: 'Elegís qué cuentas publicitarias seguir.', Icon: AccountBalanceWalletOutlinedIcon },
  link_units: { title: 'Vínculos', text: 'Cada anuncio queda con su unidad.', Icon: LinkOutlinedIcon },
  results: { title: 'Resultados', text: 'Ves el gasto y los contactos de cada unidad.', Icon: InsightsOutlinedIcon },
  create: { title: 'Crear campañas', text: 'Vas a poder crearlas desde acá.', Icon: CampaignOutlinedIcon },
};

function whyConnectSlides(steps) {
  const accountStep = steps.find((step) => step.key === 'ad_account');
  const linkStep = steps.find((step) => step.key === 'link_units');
  return [
    accountStep ? { ...CAPABILITIES.ad_account, testId: `meta-ads-step-${accountStep.key}` } : null,
    linkStep ? { ...CAPABILITIES.link_units, testId: `meta-ads-step-${linkStep.key}` } : null,
    CAPABILITIES.results,
    CAPABILITIES.create,
  ].filter(Boolean);
}

/** Una capacidad por vez: el botón de conectar no compite con cuatro tarjetas. */
function ConnectWhyCarousel({ slides, progressLabel }) {
  const [index, setIndex] = useState(0);
  const count = slides.length;
  const safe = count ? Math.min(index, count - 1) : 0;
  const slide = slides[safe];
  if (!slide) return null;
  const go = (next) => setIndex((current) => {
    const base = Math.min(current, count - 1);
    return (base + next + count) % count;
  });
  const { title, text, Icon, testId } = slide;
  const body = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 48 }} aria-live="polite">
      <Box sx={metaAdsCapabilityIconSx} aria-hidden>
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={metaAdsCardTitleSx}>{title}</Typography>
        <Typography sx={metaAdsMutedTextSx}>{text}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={metaAdsWhyConnectSx}
      role="region"
      aria-roledescription="carrusel"
      aria-label="Para qué conectar"
      data-testid="meta-ads-why-connect"
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography component="h3" sx={metaAdsWhyConnectTitleSx}>Para qué conectar</Typography>
        <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} aria-hidden>·</Typography>
        <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }}>{progressLabel}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'stretch' }}>
        <IconButton aria-label="Anterior" onClick={() => go(-1)} sx={metaAdsIconButtonSx}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {testId ? (
            <Box component="ol" sx={metaAdsPlainListSx}>
              <Box component="li" data-testid={testId}>{body}</Box>
            </Box>
          ) : body}
        </Box>
        <IconButton aria-label="Siguiente" onClick={() => go(1)} sx={metaAdsIconButtonSx}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {slides.map((item, i) => (
          <Box
            key={item.title}
            component="button"
            type="button"
            aria-current={i === safe ? 'true' : undefined}
            aria-label={item.title}
            onClick={() => setIndex(i)}
            sx={{
              width: 24,
              height: 20,
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&::before': {
                content: '""',
                width: i === safe ? 16 : 6,
                height: 6,
                borderRadius: '999px',
                bgcolor: i === safe ? colors.primary : 'rgba(255,255,255,0.28)',
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}


function StepIcon({ step, index }) {
  if (step.tone === 'done') return <CheckCircleIcon sx={metaAdsStepIconSx('done')} aria-hidden />;
  if (step.tone === 'warning') return <ErrorOutlineIcon sx={metaAdsStepIconSx('warning')} aria-hidden />;
  return <Box sx={metaAdsStepNumberSx} aria-hidden>{index + 1}</Box>;
}

function StepStatus({ step }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {step.badge ? (
        <Box component="span" sx={getMetaAdsStatusPillSx('warning')} data-testid={`meta-ads-step-${step.key}-badge`}>{step.badge}</Box>
      ) : null}
      <Box component="span" sx={getMetaAdsStatusPillSx(step.tone)}>{step.label}</Box>
    </Box>
  );
}

/**
 * Puesta en marcha de Campañas: solo mientras falta conectar Meta o elegir
 * cuenta. Con Meta conectado y una cuenta seguida el panel abre en Campañas
 * (`metaAdsOpensInCampaigns`) y la conexión / cuentas / desconectar viven en
 * «Configuración de Meta» (⚙). Sin Meta, conectar es la acción; lo que habilita
 * se recorre en el carrusel «Para qué conectar». El cuerpo de cada paso lo arma
 * el panel (`renderStepBody`).
 *
 * @param {{ checklist: object, renderStepBody?: (step: object) => React.ReactNode }} props
 */
export default function MetaAdsChecklist({ checklist, renderStepBody }) {
  const steps = checklist.steps;
  const current = steps.find((step) => !step.done) || null;
  const doneSteps = steps.filter((step) => step.done);
  const laterSteps = steps.filter((step) => !step.done && step !== current);
  const awaitingFirstConnect = current?.key === 'connect' && current.status === 'pending';
  const connectFirst = current?.key === 'connect';
  const percent = checklist.total ? Math.round((checklist.done / checklist.total) * 100) : 0;
  const progressLabel = `${checklist.done} de ${checklist.total} listos`;

  const renderBody = (step) => (renderStepBody ? renderStepBody(step) : null);

  // Sin Meta: un solo bloque centrado (ícono, título, texto, botón); «0 de 3 listos» ya dice que falta,
  // así que la píldora «Pendiente» no se repite.
  const connectHero = current ? (
    <Box component="ol" sx={{ ...metaAdsPlainListSx, flex: 1, minHeight: 0 }}>
      <Box component="li" data-testid={`meta-ads-step-${current.key}`} sx={metaAdsConnectHeroSx}>
        <Box sx={metaAdsConnectHeroIconSx} aria-hidden>
          <CampaignOutlinedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography component="h3" sx={metaAdsConnectHeroTitleSx}>{current.title}</Typography>
        {renderBody(current)}
      </Box>
    </Box>
  ) : null;

  const hero = current ? (
    <Box component="ol" sx={metaAdsPlainListSx}>
      <Box component="li" data-testid={`meta-ads-step-${current.key}`} sx={metaAdsCurrentStepCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h3" sx={metaAdsCardTitleSx}>{current.title}</Typography>
          <StepStatus step={current} />
        </Box>
        {renderBody(current)}
      </Box>
    </Box>
  ) : null;

  const compact = (group) => (group.length ? (
    <Box sx={{ ...metaAdsCardSx, gap: 0, py: 0.5 }}>
      <Box component="ol" sx={metaAdsPlainListSx}>
        {group.map((step, index) => (
          <Box
            component="li"
            key={step.key}
            data-testid={`meta-ads-step-${step.key}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              py: 1.25,
              borderTop: index > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
            }}
          >
            <StepIcon step={step} index={steps.indexOf(step)} />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={metaAdsStepTitleSx}>{step.title}</Typography>
                <StepStatus step={step} />
              </Box>
              {renderBody(step)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  ) : null);

  if (awaitingFirstConnect) {
    return (
      <Box data-testid="meta-ads-checklist" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {connectHero}
        <ConnectWhyCarousel slides={whyConnectSlides(steps)} progressLabel={progressLabel} />
      </Box>
    );
  }

  return (
    <Box data-testid="meta-ads-checklist" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h3" sx={metaAdsCardTitleSx}>Puesta en marcha</Typography>
          <Typography sx={metaAdsMutedTextSx}>{progressLabel}</Typography>
        </Box>
        {percent > 0 ? (
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={metaAdsProgressSx}
            aria-label={`Puesta en marcha: ${progressLabel}`}
          />
        ) : null}
      </Box>

      {connectFirst ? hero : compact(doneSteps)}
      {connectFirst ? compact(laterSteps) : hero}
      {connectFirst ? null : compact(laterSteps)}
    </Box>
  );
}

/** Skeleton fiel a la primera pantalla: bloque de conectar centrado y el carrusel debajo. */
export function MetaAdsChecklistSkeleton() {
  return (
    <Box data-testid="meta-ads-checklist-skeleton" aria-busy="true" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ ...metaAdsConnectHeroSx, borderColor: 'rgba(255,255,255,0.08)' }} aria-hidden>
        <Skeleton variant="circular" width={64} height={64} sx={{ ...metaAdsSkeletonSx, mb: 0.5 }} />
        <Skeleton variant="rounded" width="62%" height={26} sx={metaAdsSkeletonSx} />
        <Skeleton variant="rounded" width="100%" height={36} sx={metaAdsSkeletonSx} />
        <Skeleton variant="rounded" height={48} sx={{ ...metaAdsSkeletonSx, borderRadius: '8px', mt: 0.5 }} />
      </Box>
      <Box sx={metaAdsWhyConnectSx} aria-hidden>
        <Skeleton variant="rounded" width={200} height={14} sx={metaAdsSkeletonSx} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'stretch' }}>
          <Skeleton variant="circular" width={28} height={28} sx={metaAdsSkeletonSx} />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 48 }}>
            <Skeleton variant="rounded" width={40} height={40} sx={{ ...metaAdsSkeletonSx, borderRadius: '10px', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Skeleton variant="rounded" width="42%" height={18} sx={metaAdsSkeletonSx} />
              <Skeleton variant="rounded" width="78%" height={14} sx={metaAdsSkeletonSx} />
            </Box>
          </Box>
          <Skeleton variant="circular" width={28} height={28} sx={metaAdsSkeletonSx} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={i === 0 ? 16 : 6} height={6} sx={{ ...metaAdsSkeletonSx, borderRadius: '999px' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
